import {useEffect, useMemo, useRef, useState} from 'react';
import {BackendApi} from '../../api/api.ts';

const INACTIVITY_TIMEOUT = 2 * 60 * 1000;
const POLL_INTERVAL = 1000;
const ACTIVITY_CHECK_INTERVAL = 5000;
const MAX_EMPTY_RETRIES = 120;

interface PaymentProviderSyncStatus {
    hasJobStarted: boolean;
    progress: number;
    totalTransactions: number;
    error: string;
    isFinished: boolean;
}

export function usePaymentProviderSync(
    syncJobsGroupId: string | null | undefined,
    token: string,
): PaymentProviderSyncStatus {
    const [hasJobStarted, setHasJobStarted] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [totalTransactions, setTotalTransactions] = useState<number>(0);
    const [error, setError] = useState<string>('');
    const [isFinished, setIsFinished] = useState<boolean>(false);

    const lastActivityRef = useRef<number>(Date.now());
    const backendApi = useMemo(() => new BackendApi(token), [token]);

    useEffect(() => {
        if (!syncJobsGroupId || isFinished || error) return;

        lastActivityRef.current = Date.now();
        let emptyRetryCount = 0;

        const poll = async () => {
            try {
                const jobProgressData = await backendApi.GetImportJobProgress(syncJobsGroupId);

                if (!jobProgressData) {
                    if (++emptyRetryCount > MAX_EMPTY_RETRIES) {
                        setError('We konden geen synchronisatie-opdrachten vinden. Probeer het over een ogenblik opnieuw of neem contact op met de klantenservice. ERR-49301');
                    }
                    return;
                }

                emptyRetryCount = 0;

                lastActivityRef.current = Date.now();
                setHasJobStarted(true);

                const total = jobProgressData.total_transactions || 0;
                const processed = jobProgressData.processed_transactions || 0;

                const calculatedProgress = total > 0 ? Math.round((processed / total) * 100) : 0;

                setProgress(calculatedProgress);
                setTotalTransactions(total);

                if (jobProgressData.status === 'FAILED') {
                    setError('Er is iets misgegaan tijdens het ophalen van je gegevens. Ons team is op de hoogte gebracht en zal dit probleem binnenkort onderzoeken. Voor vragen kun je altijd contact met ons opnemen! ERR-38394');
                } else if (jobProgressData.status === 'FINISHED') {
                    setProgress(100);
                    setIsFinished(true);
                }
            } catch (err) {
                console.error('Error polling payment provider import job progress:', err);
            }
        };

        const pollInterval = setInterval(poll, POLL_INTERVAL);
        const activityCheck = setInterval(() => {
            if (Date.now() - lastActivityRef.current > INACTIVITY_TIMEOUT) {
                setError('Er is iets misgegaan tijdens het ophalen van je gegevens. Ons team is op de hoogte gebracht en zal dit probleem binnenkort onderzoeken. Voor vragen kun je altijd contact met ons opnemen! Je kunt het in de tussentijd nog een keer proberen. ERR-94303');
            }
        }, ACTIVITY_CHECK_INTERVAL);

        return () => {
            clearInterval(pollInterval);
            clearInterval(activityCheck);
        };
    }, [syncJobsGroupId, isFinished, error, backendApi]);

    return {hasJobStarted, progress, totalTransactions, error, isFinished};
}
